import React, { useState, useEffect } from "react";
import { fetchPortofolios, fetchPortofolioCategories } from "../../../../api/portofolioAPI";
import { isValidUrl, placeholderImage } from "../../../invalidImage";

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";

const resolveImg = (url) => {
  if (!url) return placeholderImage;
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};

const PortofolioPage = () => {
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchPortofolios(), fetchPortofolioCategories()])
      .then(([items, cats]) => {
        setData(items);
        setCategories(cats);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filteredData = data
    .filter((item) => item.status === 1 || item.status === "1")
    .filter((item) => activeCategory === "all" || item.type === activeCategory)
    .sort((a, b) => {
      // featured items first
      if ((b.is_featured ? 1 : 0) !== (a.is_featured ? 1 : 0))
        return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
      const dateA = a.created_at ? new Date(a.created_at) : 0;
      const dateB = b.created_at ? new Date(b.created_at) : 0;
      return dateB - dateA || (b.id - a.id);
    });

  const tabs = [{ key: "all", label: "All" }, ...categories.map((c) => ({ key: c, label: c }))];

  return (
    <div>
      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveCategory(tab.key)}
            className={`px-4 py-1.5 text-xs font-bold rounded-xl transition-all duration-200 ${
              activeCategory === tab.key
                ? "bg-blue-600 text-white shadow-lg shadow-blue-900/30"
                : "bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-600"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <LoadingGrid />
      ) : filteredData.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredData.map((item, i) => (
            <PortoCard key={i} item={item} />
          ))}
        </div>
      )}
    </div>
  );
};

const PortoCard = ({ item }) => {
  const href = /^https?:\/\//i.test(item.link) ? item.link : `https://${item.link}`;
  const imgSrc = resolveImg(isValidUrl(item.img) ? item.img : null);
  const isFeatured = item.is_featured === 1 || item.is_featured === true;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative h-56 overflow-hidden rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-blue-600/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-900/20 block"
    >
      {/* Background image */}
      <div
        className="absolute inset-0 transition-transform duration-500 group-hover:scale-105 bg-cover bg-center"
        style={{ backgroundImage: `url(${imgSrc})` }}
      />
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 group-hover:from-black/95 transition-all duration-300" />

      {/* Badges top-left */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5">
        {item.type && (
          <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest bg-blue-600/80 text-white backdrop-blur-sm">
            {item.type}
          </span>
        )}
        {isFeatured && (
          <span className="w-6 h-6 rounded-lg bg-yellow-500/80 backdrop-blur-sm flex items-center justify-center" title="Featured">
            <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </span>
        )}
      </div>


      {/* Open icon */}
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </div>
      </div>

      {/* Bottom content */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="text-white font-bold text-sm leading-snug group-hover:text-blue-300 transition-colors">{item.name}</h3>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-0.5 w-6 rounded-full bg-blue-500 group-hover:w-12 transition-all duration-300" />
          <span className="text-[10px] text-zinc-400 font-semibold uppercase tracking-widest">View Project</span>
        </div>
      </div>
    </a>
  );
};

const LoadingGrid = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="h-56 rounded-2xl bg-zinc-800/50 border border-zinc-700/50 animate-pulse" />
    ))}
  </div>
);

const EmptyState = () => (
  <div className="py-20 flex flex-col items-center justify-center text-zinc-600">
    <svg className="w-10 h-10 mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
    <p className="text-sm font-semibold">No portfolio found</p>
  </div>
);

export default PortofolioPage;
